import '../../datadog';
import '../../hotjar-init';

import { Provider } from '@bitrise/bitkit';
import { BitkitProvider } from '@bitrise/bitkit-v2';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ComponentType, createRef, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { AlertProvider } from '@/hooks/useAlert';
import Status500ErrorPageContent from '@/pages/error/ErrorPage/Status500ErrorPageContent';
import { dataDogErrorHandler } from '@/utils/dataDogErrorHandler';
import { globalProps } from '@/utils/globalProps';
import { queryClient } from '@/utils/query';

import { GlobalPropsContext } from './GlobalPropsContext';
import TooltipPortal from './TooltipPortal';

export const Layout = ({ children }: { children: ReactNode }) => {
  const toastPortalRef = createRef<HTMLDivElement>();
  return (
    <Provider toastOptions={{ offsetTop: '72px', offsetRight: '32px', portalRef: toastPortalRef }} resetCSS={false}>
      <BitkitProvider>
        <TooltipPortal ref={toastPortalRef} />
        <ErrorBoundary FallbackComponent={Status500ErrorPageContent} onError={dataDogErrorHandler}>
          <QueryClientProvider client={queryClient}>
            <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
            <AlertProvider>
              <GlobalPropsContext.Provider value={globalProps}>{children}</GlobalPropsContext.Provider>
            </AlertProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </BitkitProvider>
    </Provider>
  );
};

export const withLayout = <P extends object>(WrappedComponent: ComponentType<P>) => {
  const WithLayout: ComponentType<P> = (props) => (
    <Layout>
      <WrappedComponent {...props} />
    </Layout>
  );
  return WithLayout;
};
